import { SearchItem } from '@yiluhub/yilu-amp-types';
import { TFunction } from 'i18next';

import { extractAmpProductAttribute } from 'utils/ampUtils';

import { FareType, PassType } from 'modules/eurail/utils/types';

export type EurailProductDetails = {
  price?: number;
  currency: string;
  validityPeriodMonth: number;
  numberOfTravelDays: number;
  numberOfPassengers: number;
  numberOfAdults: number;
  numberOfYouths: number;
  numberOfChildren: number;
  numberOfSeniors: number;
  fareClass: FareType;
  pass?: PassType;
  productType?: string;
  id: string;
  catalogId: string;
  serviceProvider?: string;
  termsUrl?: string;
  to?: string;
  searchResultId: string;
  priceOptionId: string;
  productName: string;
};

export function getTravelDaysLabel(t: TFunction, numberOfTravelDays: number): string {
  return numberOfTravelDays > 1
    ? `${numberOfTravelDays} ${t('eurail.days.text')}`
    : `${numberOfTravelDays} ${t('eurail.day.text')}`;
}

export function getTravelDurationLabel(t: TFunction, validityPeriodMonth: number): string {
  if (validityPeriodMonth === 0) {
    return t('eurail.travelduration.in.row.text');
  }
  if (validityPeriodMonth === 1) {
    return t('eurail.travelduration.month.text');
  }
  return t('eurail.travelduration.months.text', { months: validityPeriodMonth });
}

export const getEurailProductDetails = (
  data: SearchItem | null | undefined,
): EurailProductDetails => {
  if (!data) {
    return getDefaultDetails();
  }

  const price = data.item.priceOptions ? data.item.priceOptions[0].price.amount / 100 : undefined;
  const currency = data.item.priceOptions ? data.item.priceOptions[0].price.currency : 'EUR';
  const priceOptionId = data.item.priceOptions ? data.item.priceOptions[0].id : '';
  const productName = data.item.productDetails[0]?.productDescription.name || '';

  const validityPeriodMonth = extractAmpProductAttribute(data, 'validity_period_month');
  const numberOfTravelDays = extractAmpProductAttribute(data, 'number_of_travel_days');
  const numberOfPassengers = extractAmpProductAttribute(data, 'number_of_passengers');
  const numberOfAdults = extractAmpProductAttribute(data, 'number_of_adults');
  const numberOfYouths = extractAmpProductAttribute(data, 'number_of_youths');
  const numberOfChildren = extractAmpProductAttribute(data, 'number_of_children');
  const numberOfSeniors = extractAmpProductAttribute(data, 'number_of_seniors');
  const productType = extractAmpProductAttribute(data, 'product_type');
  const passType = extractAmpProductAttribute(data, 'pass_type') as PassType;
  const to = extractAmpProductAttribute(data, 'to');
  const fareClass =
    (extractAmpProductAttribute(data, 'eurails_fare_class') as FareType) || FareType.SECOND_CLASS;

  return {
    id: data.id,
    catalogId: data.item.catalogId,
    price,
    currency: currency,
    serviceProvider: extractAmpProductAttribute(data, 'service_provider'),
    validityPeriodMonth: validityPeriodMonth ? parseInt(validityPeriodMonth) : 0,
    numberOfTravelDays: numberOfTravelDays ? parseInt(numberOfTravelDays) : 0,
    numberOfPassengers: numberOfPassengers ? parseInt(numberOfPassengers) : 0,
    numberOfChildren: numberOfChildren ? parseInt(numberOfChildren) : 0,
    numberOfAdults: numberOfAdults ? parseInt(numberOfAdults) : 0,
    numberOfYouths: numberOfYouths ? parseInt(numberOfYouths) : 0,
    numberOfSeniors: numberOfSeniors ? parseInt(numberOfSeniors) : 0,

    fareClass,
    productType,
    termsUrl: extractAmpProductAttribute(data, 'terms_url'),
    searchResultId: data.id,
    priceOptionId,
    productName,
    to,
    pass: passType,
  };
};

const getDefaultDetails = (): EurailProductDetails => {
  return {
    id: '',
    price: undefined,
    catalogId: '',
    serviceProvider: undefined,
    validityPeriodMonth: 0,
    numberOfTravelDays: 0,
    numberOfPassengers: 0,
    numberOfChildren: 0,
    numberOfAdults: 0,
    numberOfYouths: 0,
    numberOfSeniors: 0,
    fareClass: FareType.SECOND_CLASS,
    pass: undefined,
    productType: undefined,
    currency: 'EUR',
    termsUrl: undefined,
    to: undefined,
    searchResultId: '',
    priceOptionId: '',
    productName: '',
  };
};
